import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import Logomark from "./Logomark";

const sections = [
  { id: "about", key: "about" },
  { id: "services", key: "services" },
  { id: "capabilities", key: "capabilities" },
  { id: "projects", key: "projects" },
  { id: "presence", key: "presence" },
  { id: "careers", key: "careers" },
  { id: "contact", key: "contact" },
];

export default function Navigation() {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <Logomark size={38} className="transition-transform duration-500 group-hover:-translate-y-0.5" />
          <div className="hidden sm:block leading-none text-left border-l border-white/10 pl-3">
            <div className="font-display font-black tracking-tight text-[15px] text-white">
              DEPP C&amp;M
            </div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-[#8B939C] mt-1">
              S.A. DE C.V.
            </div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-link-${s.id}`}
              onClick={() => scrollTo(s.id)}
              className="px-4 py-2 text-[13px] font-medium text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 uppercase tracking-wider"
            >
              {t.nav[s.key]}
            </button>
          ))}
          <Link
            to="/capacidades"
            data-testid="nav-link-portfolio"
            className="px-4 py-2 text-[13px] font-medium text-[#D4AF37] hover:text-[#F0C84B] transition-colors duration-300 uppercase tracking-wider flex items-center gap-1"
          >
            <span>{t.nav.portfolio}</span>
            <span className="w-1 h-1 bg-[#D4AF37] rounded-full" />
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#F4F4F5] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
          >
            <Languages className="w-3.5 h-3.5" />
            {lang.toUpperCase()}
          </button>
          <button
            onClick={() => scrollTo("contact")}
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center px-5 py-2.5 bg-[#D4AF37] text-black text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#F0C84B] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#0A0A0B]/98 backdrop-blur-xl border-b border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {sections.map((s) => (
                <button
                  key={s.id}
                  data-testid={`mobile-nav-${s.id}`}
                  onClick={() => scrollTo(s.id)}
                  className="text-left px-2 py-3 text-sm text-[#F4F4F5] hover:text-[#D4AF37] border-b border-white/5 uppercase tracking-wider"
                >
                  {t.nav[s.key]}
                </button>
              ))}
              <Link
                to="/capacidades"
                data-testid="mobile-nav-portfolio"
                onClick={() => setOpen(false)}
                className="text-left px-2 py-3 text-sm text-[#D4AF37] hover:text-[#F0C84B] border-b border-white/5 uppercase tracking-wider"
              >
                {t.nav.portfolio}
              </Link>
              <button
                data-testid="mobile-nav-cta"
                onClick={() => scrollTo("contact")}
                className="mt-4 px-5 py-3 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em]"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
